import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import { toast } from 'react-toastify';
import useCompanyState from './company.state';
import type {
  IJobDescriptionPeran,
  IJobDescriptionPeranPaginated,
  ListJobDescriptionPeranParams,
} from '@/types/job-description-peran.types';

export interface IJobDescription {
  id?: number | string;
  tenant_id?: number | string;
  org_chart_node_id?: number;
  org_chart_node?: {
    id: number;
    name: string;
  };
  org_key_activity_id?: number;
  org_key_activity?: {
    id: number;
    name: string;
  };
  name?: string;
  report?: string;
  frequency?: string;
  kpi?: string;
  kpi_measurement?: string;
  report_file?: any;
  is_published?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface IJobDescriptionPaginatedResponse {
  content: IJobDescription[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export interface UploadedFileMeta {
  disk: string;
  path: string;
  filename: string;
  original_name: string;
  mime_type: string;
  size: number;
  extension: string;
  url: string;
  uploaded_at: string;
  content_type: string;
}

interface JobDescriptionState {
  jobDescriptions: IJobDescription[];
  paginatedData: IJobDescriptionPaginatedResponse | null;
  selectedJobDescription: IJobDescription | null;
  isLoading: boolean;
  error: unknown;
  jobDescriptionsFull: IJobDescription[];
  getJobDescriptionsFull: (tenantId: string | number) => Promise<void>;
  clearJobDescriptions: () => void;
  createJobDescription: (tenantId: string | number, payload: Partial<IJobDescription>) => Promise<void>;
  getJobDescriptions: (
    tenantId: string | number,
    params?: {
      page?: number;
      page_size?: number;
      order?: 'asc' | 'desc';
      order_by?: string;
      org_chart_node_id?: number;
      org_key_activity_id?: number;
      name?: string;
      published?: boolean;
    },
  ) => Promise<IJobDescriptionPaginatedResponse | void>;
  getJobDescriptionDetail: (tenantId: string | number, id: number | string) => Promise<IJobDescription | void>;
  updateJobDescription: (
    tenantId: string | number,
    id: number | string,
    payload: Partial<IJobDescription> | FormData,
  ) => Promise<void>;

  deleteJobDescription: (tenantId: string | number, id: number | string) => Promise<void>;
  bulkPublishJobDescriptions: (tenantId: string | number, isPublished: boolean) => Promise<void>;
  addReportFile: (tenantId: string | number, id: number | string, reportFile: any) => Promise<UploadedFileMeta>;
  addKpiMeasurement: (
    tenantId: string | number,
    id: number | string,
    kpi: string,
    kpiMeasurement: string,
  ) => Promise<void>;
  peranList: IJobDescriptionPeran[];
  peranPaginated: IJobDescriptionPeranPaginated | null;
  peranLoading: boolean;
  getJobDescriptionPeran: (
    tenantId: string | number,
    params?: ListJobDescriptionPeranParams,
  ) => Promise<IJobDescriptionPeranPaginated | void>;
}

const useJobDescriptionState = create<JobDescriptionState>((set, get) => ({
  jobDescriptions: [],
  jobDescriptionsFull: [],
  paginatedData: null,
  selectedJobDescription: null,
  isLoading: false,
  error: null,

  peranList: [],
  peranPaginated: null,
  peranLoading: false,

  clearJobDescriptions: () => set({ jobDescriptions: [], paginatedData: null }),

  createJobDescription: async (tenantId, payload) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}`;
      await axiosInstance.post(url, payload);
      await get().getJobDescriptions(tenantId);
      toast.success('Deskripsi Pekerjaan berhasil dibuat');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getJobDescriptions: async (tenantId, params) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}`;
      const { data } = await axiosInstance.get(url, { params });

      const paginatedResponse: IJobDescriptionPaginatedResponse = {
        content: data.data?.content || [],
        totalElements: data.data?.totalElements || 0,
        totalPages: data.data?.totalPages || 0,
        size: data.data?.size || 10,
        number: data.data?.number || 0,
      };

      set({
        jobDescriptions: data.data?.content || [],
        paginatedData: paginatedResponse,
      });

      return paginatedResponse;
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getJobDescriptionsFull: async tenantId => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}`;
      const { data } = await axiosInstance.get(url, { params: { page_size: 9999 } });

      set({
        jobDescriptionsFull: data.data?.content || [],
      });
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getJobDescriptionDetail: async (tenantId, id) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}/${id}`;
      const { data } = await axiosInstance.get(url);
      set({ selectedJobDescription: data.data });
      return data.data;
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  updateJobDescription: async (tenantId, id, payload) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}/${id}`;

      const isFormData = payload instanceof FormData;
      const { data } = await axiosInstance.put(url, payload, {
        headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
      });

      set(state => ({
        jobDescriptions: state.jobDescriptions.map(jd => (jd.id === id ? { ...jd, ...data.data } : jd)),
        selectedJobDescription: data.data,
      }));
      toast.success('Deskripsi Pekerjaan berhasil diperbarui');
    } catch (error) {
      set({ error });
      errorHandler(error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteJobDescription: async (tenantId, id) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}/${id}`;
      await axiosInstance.delete(url);

      set(state => ({
        jobDescriptions: state.jobDescriptions.filter(jd => jd.id !== id),
        selectedJobDescription: state.selectedJobDescription?.id === id ? null : state.selectedJobDescription,
      }));

      toast.success('Deskripsi Pekerjaan berhasil dihapus');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  bulkPublishJobDescriptions: async (tenantId: string | number, isPublished: boolean) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}/bulk-publish`;
      await axiosInstance.put(url, { published: isPublished });

      set(state => ({
        jobDescriptions: state.jobDescriptions.map(jd => ({
          ...jd,
          is_published: isPublished,
        })),
      }));

      toast.success(
        isPublished
          ? 'Semua Deskripsi Pekerjaan berhasil dipublikasikan'
          : 'Semua Deskripsi Pekerjaan berhasil dibatalkan publikasinya',
      );
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  addReportFile: async (tenantId, id, file) => {
    set({ isLoading: true });
    try {
      const { uploadCompanyMediaDirect } = await import('@/hooks/useCompanyProfile');

      const uploadRes = (await uploadCompanyMediaDirect(file, id, 'Report Template')) as any;
      const uploadedFile = uploadRes?.data?.[0] || uploadRes?.[0] || uploadRes;

      const formattedFile = {
        disk: uploadedFile.disk || 'minio',
        path: uploadedFile.path,
        filename: uploadedFile.filename,
        original_name: uploadedFile.original_name,
        mime_type: uploadedFile.mime_type,
        size: uploadedFile.size,
        extension: uploadedFile.extension,
        url: uploadedFile.url,
        uploaded_at: uploadedFile.uploaded_at || new Date().toISOString(),
        content_type: uploadedFile.content_type || uploadedFile.mime_type,
      };

      return formattedFile;
    } catch (error) {
      set({ error });
      errorHandler(error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  addKpiMeasurement: async (tenantId, id, kpi, kpiMeasurement) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}/${id}`;
      const { data } = await axiosInstance.put(url, {
        kpi,
        kpi_measurement: kpiMeasurement,
      });

      set(state => ({
        jobDescriptions: state.jobDescriptions.map(jd =>
          jd.id === id
            ? {
                ...jd,
                kpi: data.data.kpi,
                kpi_measurement: data.data.kpi_measurement,
              }
            : jd,
        ),
      }));

      toast.success('Pengukuran KPI berhasil ditambahkan');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getJobDescriptionPeran: async (tenantId, params) => {
    set({ peranLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}/peran`;
      const { data } = await axiosInstance.get(url, { params });
      const paginated: IJobDescriptionPeranPaginated = {
        content: data?.data?.content ?? [],
        totalElements: data?.data?.totalElements ?? 0,
        totalPages: data?.data?.totalPages ?? 0,
        size: data?.data?.size ?? 10,
        number: data?.data?.number ?? 0,
      };
      set({
        peranList: paginated.content,
        peranPaginated: paginated,
      });
      return paginated;
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ peranLoading: false });
    }
  },
}));

export default useJobDescriptionState;
